import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { morph } from '@theme/morph';
import { fetchConfig } from '@theme/utilities';

// History stack for navigation
const historyStack = [];

/**
 * Rewrites a Shopify CDN image URL to request a specific width
 * @param {string} url - The original image URL
 * @param {number} targetWidth - The desired width
 * @returns {string} - The modified URL
 */
function resizeShopifyImageUrl(url, targetWidth) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('cdn.shopify.com')) return url;

  try {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set('width', targetWidth.toString());
    urlObj.searchParams.delete('height');
    return urlObj.href;
  } catch {
    if (url.includes('width=')) {
      return url.replace(/width=\d+/, `width=${targetWidth}`);
    }
    return url + (url.includes('?') ? '&' : '?') + `width=${targetWidth}`;
  }
}

/**
 * Optimizes images in fetched content for popup display
 * @param {Element} content - The content element containing images
 * @param {number} maxWidth - Maximum display width (default: 950)
 */
function optimizePopupImages(content, maxWidth = 950) {
  const width1x = maxWidth;
  const width2x = maxWidth * 2;

  content.querySelectorAll('img').forEach(img => {
    const src = img.getAttribute('src');
    if (src) {
      img.setAttribute('src', resizeShopifyImageUrl(src, width1x));

      // Create optimized srcset for retina
      const srcset = img.getAttribute('srcset');
      if (srcset) {
        const src2x = resizeShopifyImageUrl(src, width2x);
        img.setAttribute('srcset', `${resizeShopifyImageUrl(src, width1x)} 1x, ${src2x} 2x`);
      }
    }

    // Add sizes attribute for popup context
    img.setAttribute('sizes', `(max-width: 749px) 100vw, ${maxWidth}px`);
  });
}

/**
 * Page Popup Trigger Component
 * Handles clicking on elements that should open a page in a popup
 * 
 * @extends Component
 */
export class PagePopupTrigger extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  
  /** @type {Map<string, Document>} */
  #pageCache = new Map();

  /**
   * Handle click event on trigger element
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    
    const pageHandle = this.dataset.pageHandle;
    const productHandle = this.dataset.productHandle;
    const pageUrl = this.dataset.pageUrl || 
                   (productHandle ? this.href || `/products/${productHandle}` : `/pages/${pageHandle}`);
    
    if (!pageHandle && !pageUrl && !productHandle) {
      console.error('PagePopupTrigger: Missing page-handle, product-handle or page-url data attribute');
      return;
    }

    try {
      const pageContent = await this.fetchPageContent(pageUrl);
      if (pageContent) {
        await this.showPageInPopup(pageContent, productHandle || pageHandle || 'page', false, pageUrl);
      }
    } catch (error) {
      console.error('Error loading page content:', error);
    }
  };

  /**
   * Fetch page content with caching
   * @param {string} pageUrl - The URL of the page to fetch
   * @returns {Promise<Document|null>}
   */
  async fetchPageContent(pageUrl) {
    // Locale-aware: aktiven Sprach-Präfix (z. B. /de) voranstellen, sonst liefert
    // Shopify die Primärsprache (EN) statt der übersetzten Seite.
    const localeRoot = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    if (localeRoot && pageUrl && pageUrl.startsWith('/') && !pageUrl.startsWith(localeRoot + '/')) {
      pageUrl = localeRoot + pageUrl;
    }

    // Check cache first
    if (this.#pageCache.has(pageUrl)) {
      return this.#pageCache.get(pageUrl);
    }

    // Abort any pending requests
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(pageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch page: HTTP ${response.status}`);
      }

      const responseText = await response.text();
      const pageDocument = new DOMParser().parseFromString(responseText, 'text/html');
      
      // Cache the result
      this.#pageCache.set(pageUrl, pageDocument);
      
      return pageDocument;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Clean and normalize title text
   * @param {string} title - The raw title text
   * @returns {string} - Cleaned title
   */
  cleanTitle(title) {
    if (!title) return 'Page Content';
    
    // Decode HTML entities first
    const textarea = document.createElement('textarea');
    textarea.innerHTML = title;
    title = textarea.value;
    
    // Trim whitespace
    title = title.trim();
    
    // Remove extra whitespace
    title = title.replace(/\s+/g, ' ');
    
    // Remove site name from title if it comes from the <title> tag
    const separators = [' – ', ' - ', ' | ', ' · ', ' :: '];
    for (const separator of separators) {
      if (title.includes(separator)) {
        const parts = title.split(separator);
        // Usually the page title is first, site name is last
        title = parts[0].trim();
        break;
      }
    }
    
    return title;
  }
  
  /**
   * Clean URL by removing query parameters
   * @param {string} url - The URL to clean
   * @returns {string} - Clean URL without parameters
   */
  cleanUrl(url) {
    if (!url) return '';
    try {
      const urlObj = new URL(url, window.location.origin);
      // Return only the origin + pathname (no search params or hash)
      return urlObj.origin + urlObj.pathname;
    } catch (error) {
      // If URL parsing fails, try to remove everything after ?
      const questionMarkIndex = url.indexOf('?');
      const hashIndex = url.indexOf('#');
      
      if (questionMarkIndex > -1) {
        return url.substring(0, questionMarkIndex);
      } else if (hashIndex > -1) {
        return url.substring(0, hashIndex);
      }
      
      return url;
    }
  }

  /**
   * Show the page content in a popup
   * @param {Document} pageDocument - The parsed page document
   * @param {string} pageHandle - The page handle for identification
   * @param {boolean} isNested - Whether this is a nested popup (product within bundle)
   * @param {string} pageUrl - The URL of the page
   * @param {boolean} isFromVariantBundle - Whether this was triggered from variant-selection bundle item
   */
  async showPageInPopup(pageDocument, pageHandle, isNested = false, pageUrl = '', isFromVariantBundle = false) {
    const dialog = document.getElementById('page-popup-dialog');
    
    if (!(dialog instanceof PagePopupDialog)) {
      console.error('Page popup dialog not found');
      return;
    }

    // Extract the main content from the page
    const pageContent = pageDocument.querySelector('main .shopify-section') ||
                       pageDocument.querySelector('.product__info') ||
                       pageDocument.querySelector('[data-section-type="product"]');
    
    // Try multiple sources for the title - get the FULL text content
    let pageTitle = '';
    
    // Priority 1: Main H1 in the content
    const h1InContent = pageDocument.querySelector('main h1, .product__title, h1.title');
    if (h1InContent) {
      // Get all text nodes to ensure we get the complete title
      pageTitle = h1InContent.innerText || h1InContent.textContent || '';
    }
    // Priority 2: Any H1 on the page
    else if (pageDocument.querySelector('h1')) {
      const h1 = pageDocument.querySelector('h1');
      pageTitle = h1.innerText || h1.textContent || '';
    }
    // Priority 3: Meta title or page title
    else {
      const metaTitle = pageDocument.querySelector('meta[property="og:title"]')?.content;
      const titleTag = pageDocument.querySelector('title')?.innerText || 
                      pageDocument.querySelector('title')?.textContent;
      pageTitle = metaTitle || titleTag || 'Page Content';
    }
    
    // Clean the title
    pageTitle = this.cleanTitle(pageTitle);
    
    // Clean the URL for the product link
    const cleanedUrl = this.cleanUrl(pageUrl);
    
    if (!pageContent) {
      console.error('No content found in page');
      return;
    }

    // If dialog is already open and this is a nested call, save current state to history
    if (dialog.refs.dialog.open && isNested) {
      const currentTitle = dialog.querySelector('[data-page-title]')?.innerText || 
                          dialog.querySelector('[data-page-title]')?.textContent;
      const currentContent = dialog.querySelector('[data-page-content]')?.innerHTML;
      const currentUrl = dialog.querySelector('[data-product-link]')?.href || '';
      
      if (currentTitle && currentContent) {
        historyStack.push({
          title: currentTitle,
          content: currentContent,
          url: currentUrl
        });
      }
    }
    
    // Clone and optimize images for popup display
    const clonedContent = pageContent.cloneNode(true);
    optimizePopupImages(clonedContent, 950);

    // Update dialog content with cleaned URL, variant bundle flag, and nested state
    dialog.updateContent(pageTitle, clonedContent, historyStack.length > 0, cleanedUrl, isFromVariantBundle, isNested);
    
    // Show the dialog if not already open
    if (!dialog.refs.dialog.open) {
      dialog.showDialog();
    }
    
    // Setup event listeners for nested popup triggers
    this.setupNestedTriggers(dialog);
  }
  
  /**
   * Handle back navigation in popup
   */
  static handleBack() {
    if (historyStack.length === 0) return;
    
    const dialog = document.getElementById('page-popup-dialog');
    if (!dialog) return;
    
    const previousState = historyStack.pop();
    
    // Create a wrapper to hold the HTML content
    const wrapper = document.createElement('div');
    wrapper.innerHTML = previousState.content;
    
    // Clean the URL if present
    const tempTrigger = window.pagePopupTriggerInstance || new PagePopupTrigger();
    const cleanedUrl = tempTrigger.cleanUrl(previousState.url);
    
    dialog.updateContent(previousState.title, wrapper, historyStack.length > 0, cleanedUrl);
    
    // Re-setup triggers for the restored content
    tempTrigger.setupNestedTriggers(dialog);
  }
  
  /**
   * Setup event listeners for nested popup triggers
   * @param {PagePopupDialog} dialog - The dialog containing the triggers
   */
  setupNestedTriggers(dialog) {
    const triggers = dialog.querySelectorAll('.bundle-popup-trigger, [data-product-handle], .page-popup-trigger');
    
    triggers.forEach(trigger => {
      // Remove the initialized attribute to allow re-initialization
      trigger.removeAttribute('data-popup-initialized');
      
      // Remove any existing listeners by cloning and replacing
      const newTrigger = trigger.cloneNode(true);
      trigger.parentNode.replaceChild(newTrigger, trigger);
      
      newTrigger.setAttribute('data-popup-initialized', 'true');
      newTrigger.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const productHandle = newTrigger.dataset.productHandle;
        const pageHandle = newTrigger.dataset.pageHandle;
        const pageUrl = newTrigger.href || newTrigger.dataset.pageUrl ||
                       (pageHandle ? `/pages/${pageHandle}` : null);

        if (!productHandle && !pageUrl && !pageHandle) return;

        // Check if this is from a variant-selection bundle item
        const isFromVariantBundle = newTrigger.classList.contains('variant-selection__bundle-item');

        // First check if we have local bundle data with this product's info
        const bundleDataElement = dialog.querySelector('#bundle-products-data');
        let useLocalData = false;
        let localProductData = null;
        
        if (bundleDataElement) {
          try {
            const bundleData = JSON.parse(bundleDataElement.textContent);
            localProductData = bundleData.bundledProducts.find(p => p.handle === productHandle);
            if (localProductData && localProductData.description) {
              useLocalData = true;
            }
          } catch (e) {
            console.log('Could not parse bundle data:', e);
          }
        }
        
        // Try to get the existing trigger instance to preserve cache
        // First check if there's a global instance
        const tempTrigger = window.pagePopupTriggerInstance || new PagePopupTrigger();
        
        // Only use local data for product-info views, not for adjust pages
        const isProductInfo = pageUrl && pageUrl.includes('?view=product-info');
        
        if (useLocalData && localProductData && isProductInfo) {
          // Use local data - create a document with the product description
          const parser = new DOMParser();
          
          const contentHTML = `
            <div class="product-info-popup-content">
              <div class="product-description-wrapper">
                ${localProductData.description}
              </div>
            </div>
          `;
          
          const doc = parser.parseFromString(`
            <!DOCTYPE html>
            <html>
              <head><title>${localProductData.title}</title></head>
              <body>
                <main class="main-content">
                  <div class="shopify-section">
                
                    ${contentHTML}
                  </div>
                </main>
              </body>
            </html>
          `, 'text/html');
          
          // Show the content in popup
          await tempTrigger.showPageInPopup(doc, productHandle || 'product', true, localProductData.url, isFromVariantBundle);
        } else {
          // Fallback to AJAX fetch for everything else (including adjust pages and page popups)
          const fullUrl = pageUrl || newTrigger.href;
          const pageContent = await tempTrigger.fetchPageContent(fullUrl);
          if (pageContent) {
            await tempTrigger.showPageInPopup(pageContent, productHandle || pageHandle || 'page', true, fullUrl, isFromVariantBundle);
          }
        }
      });
    });
  }
}

/**
 * Page Popup Dialog Component
 * The actual dialog that displays page content
 * 
 * @extends DialogComponent
 */
export class PagePopupDialog extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    
    const closeButton = this.querySelector('[data-close-dialog]');
    if (closeButton) {
      closeButton.addEventListener('click', (event) => {
        event.stopPropagation();
        this.closeDialog();
      });
    }
    
    // Listen for dialog close to clear history stack
    this.addEventListener(DialogCloseEvent.type, () => {
      historyStack.length = 0;
    });
  }
  
  /**
   * Update the dialog content
   * @param {string} title - The page title
   * @param {Node} content - The page content to display
   * @param {boolean} showBackButton - Whether to show the back button
   * @param {string} pageUrl - The URL of the page (for the product link)
   * @param {boolean} isFromVariantBundle - Whether this was triggered from variant-selection bundle item
   * @param {boolean} isNested - Whether this is a nested popup
   */
  updateContent(title, content, showBackButton = false, pageUrl = '', isFromVariantBundle = false, isNested = false) {
    const titleElement = this.querySelector('[data-page-title]');
    const contentContainer = this.querySelector('[data-page-content]');
    const backButton = this.querySelector('[data-back-button]');
    const closeButton = this.querySelector('[data-close-dialog]');
    const productLink = this.querySelector('[data-product-link]');
    
    if (titleElement) {
      // Use innerText first, then textContent as fallback
      // This ensures the full text is set
      if ('innerText' in titleElement) {
        titleElement.innerText = title;
      } else {
        titleElement.textContent = title;
      }
      
      // Verify the title was set correctly
      const actualTitle = titleElement.innerText || titleElement.textContent;
      if (actualTitle !== title) {
        console.warn('Title mismatch:', {
          expected: title,
          actual: actualTitle,
          expectedLength: title.length,
          actualLength: actualTitle.length
        });
        
        // Try alternative method
        titleElement.innerHTML = '';
        const textNode = document.createTextNode(title);
        titleElement.appendChild(textNode);
      }
      
      // Optional: Add wrap class for very long titles
      if (title.length > 50) {
        titleElement.classList.add('page-popup__title--wrap');
      } else {
        titleElement.classList.remove('page-popup__title--wrap');
      }
      
     
    }
    
    if (contentContainer) {
      // Use morph for smooth content update
      morph(contentContainer, content);
    }
    
    // Show/hide back button and close button based on nested state
    if (backButton) {
      backButton.style.display = showBackButton ? 'flex' : 'none';
      if (showBackButton && !backButton.hasAttribute('data-back-initialized')) {
        backButton.setAttribute('data-back-initialized', 'true');
        backButton.addEventListener('click', (event) => {
          event.stopPropagation();
          PagePopupTrigger.handleBack();
        });
      }
    }
    
    // Hide close button when back button is shown (nested popup)
    if (closeButton) {
      closeButton.style.display = showBackButton ? 'none' : 'flex';
    }
    
    // Show/hide and update product link button with clean URL
    if (productLink) {
      // Hide product link for discounts-pricing page (preorder popup)
      const isDiscountsPricingPage = pageUrl && (pageUrl.includes('/pages/discounts-pricing') || pageUrl.includes('discounts-pricing'));

      // Show product link if this is a nested popup and we have a URL (but not for discounts-pricing)
      if (isNested && pageUrl && !isDiscountsPricingPage) {
        productLink.style.display = 'inline-block';
        // Add ?view=parts parameter if this was triggered from variant-selection bundle item
        if (isFromVariantBundle) {
          const url = new URL(pageUrl, window.location.origin);
          //url.searchParams.set('view', 'parts');
          productLink.href = url.pathname + url.search;
        } else {
          productLink.href = pageUrl; // URL is already cleaned in showPageInPopup
        }
      } else {
        productLink.style.display = 'none';
      }
    }
  }
}

// Register custom elements
if (!customElements.get('page-popup-trigger')) {
  customElements.define('page-popup-trigger', PagePopupTrigger);
}

if (!customElements.get('page-popup-dialog')) {
  customElements.define('page-popup-dialog', PagePopupDialog);
}

// Export for use in other modules
export default { PagePopupTrigger, PagePopupDialog };